import * as Toast from '@radix-ui/react-toast'
import { Award, RefreshCw, Star, Sun } from 'lucide-react'
import { type ReactNode, createContext, useCallback, useContext, useState } from 'react'

type ToastVariant = 'energy' | 'xp' | 'badge' | 'info' | 'update'

interface ToastAction {
  label: string
  onClick: () => void
}

interface ToastItem {
  id: string
  message: string
  variant: ToastVariant
  duration: number
  action?: ToastAction
}

interface ToastContextValue {
  showToast: (
    message: string,
    opts?: { variant?: ToastVariant; duration?: number; action?: ToastAction },
  ) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

const variantIcons: Record<ToastVariant, ReactNode> = {
  energy: <Sun size={16} />,
  xp: <Star size={16} />,
  badge: <Award size={16} />,
  info: null,
  update: <RefreshCw size={16} />,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const showToast = useCallback(
    (
      message: string,
      opts?: { variant?: ToastVariant; duration?: number; action?: ToastAction },
    ) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      const item: ToastItem = {
        id,
        message,
        variant: opts?.variant ?? 'info',
        duration: opts?.duration ?? 4000,
        action: opts?.action,
      }
      setToasts((prev) => [...prev, item])
    },
    [],
  )

  return (
    <ToastContext.Provider value={{ showToast }}>
      <Toast.Provider swipeDirection="up">
        {children}
        {toasts.map((toast) => (
          <Toast.Root
            className="wefa-toast"
            data-variant={toast.variant}
            duration={toast.duration === 0 ? Number.POSITIVE_INFINITY : toast.duration}
            key={toast.id}
            onOpenChange={(open) => {
              if (!open) setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }}
          >
            <Toast.Description className="wefa-toast__description">
              {variantIcons[toast.variant]}
              {toast.message}
            </Toast.Description>
            {toast.action && (
              <Toast.Action altText={toast.action.label} asChild>
                <button className="wefa-toast__action" onClick={toast.action.onClick} type="button">
                  {toast.action.label}
                </button>
              </Toast.Action>
            )}
          </Toast.Root>
        ))}
        <Toast.Viewport className="wefa-toast-viewport" />
      </Toast.Provider>
    </ToastContext.Provider>
  )
}
